import React, { useState, useEffect, useRef } from 'react';
import { formatAsDate } from './Utils';
import { tns } from 'tiny-slider/src/tiny-slider';
import Search from './Search';
import FeaturedDriver from './FeaturedDriver';
import ResultStat from './Stats/ResultStat';
import ResultsTableWrapper from './ResultsTableWrapper';
import { RacesTable, DriversTable, TeamsTable, ChampionshipsTable, CircuitsTable } from './ResultsTables';
import Loader from '../../Search/Loader';

const DatabaseSummaryStats = ({
    searchTerm,
    racesData,
    driversData,
    teamsData,
    championshipsData,
    circuitsData,
    handleLink,
}) => {
    const sliderRef = useRef(null);

    const statsCssClasses = () => {
        let cssClasses = ['database-stats'];

        if (driversData.hits.length === 1) {
            cssClasses.push('database-stats--without-drivers');
        }

        return cssClasses.join(' ');
    };

    const buildResultsSlider = () => {
        const slider = tns({
            container: sliderRef.current,
            mouseDrag: true,
            nav: false,
            gutter: 30,
            loop: false,
            responsive: {
                0: {
                    items: 1,
                },
                678: {
                    items: 2,
                },
                1024: {
                    items: 3,
                },
                1334: {
                    items: driversData.hits.length === 1 ? 4 : 5,
                },
            },
        });

        slider.refresh();
    };

    useEffect(() => {
        buildResultsSlider();
    }, [driversData]);

    return (
        <div className="container">
            {driversData.hits.length === 1 && <FeaturedDriver driver={driversData.hits[0]} />}
            <div ref={sliderRef} className={statsCssClasses()}>
                <ResultStat term={searchTerm} results={racesData} type="Races" onClickHandler={handleLink} />
                {driversData.hits.length !== 1 && (
                    <ResultStat term={searchTerm} results={driversData} type="Drivers" onClickHandler={handleLink} />
                )}
                <ResultStat term={searchTerm} results={teamsData} type="Teams" onClickHandler={handleLink} />
                <ResultStat
                    term={searchTerm}
                    results={championshipsData}
                    type="Championships"
                    onClickHandler={handleLink}
                />
                <ResultStat term={searchTerm} results={circuitsData} type="Circuits" onClickHandler={handleLink} />
            </div>
        </div>
    );
};

const DatabaseWrapper = ({ term, results }) => {
    const defaultResponse = { total: { value: 0 }, hits: [] };
    const [searchTerm, setSearchTerm] = useState(term);
    const [racesData, setRacesData] = useState(defaultResponse);
    const [driversData, setDriversData] = useState(defaultResponse);
    const [teamsData, setTeamsData] = useState(defaultResponse);
    const [championshipsData, setChampionshipsData] = useState(defaultResponse);
    const [circuitsData, setCircuitsData] = useState(defaultResponse);
    const [activeResultsType, setActiveResultsType] = useState(false);
    const [activeResults, setActiveResults] = useState(defaultResponse);
    const [activeResultsComponent, setActiveResultsComponent] = useState(<></>);

    const fetchResults = async () => {
        const search = new Search(searchTerm, ['race', 'driver', 'team', 'championship', 'circuit'], 999, 1);
        const { responses } = await search.getResults();

        // Sort races by date desc
        responses[0].hits.hits = responses[0].hits.hits.sort(
            (a, b) => formatAsDate(b._source.raceStartDate) - formatAsDate(a._source.raceStartDate)
        );

        // Sort teams by name asc
        responses[2].hits.hits = responses[2].hits.hits.sort(
            (a, b) => a._source.title.localeCompare(b._source.title)
        );

        // Sort drivers by last name then first name
        responses[1].hits.hits = responses[1].hits.hits.sort(
            (a, b) => a._source.title.split(' ').pop()[1].localeCompare(b._source.title.split(' ').pop()[1])
        ).sort(
            (a, b) => a._source.title.split(' ').pop()[0].localeCompare(b._source.title.split(' ').pop()[0])
        );

        // Sort championships by year desc
        responses[3].hits.hits = responses[3].hits.hits.sort(
            (a, b) => parseInt(b._source.season) - parseInt(a._source.season)
        );

        // Sort circuits by name asc
        responses[4].hits.hits = responses[4].hits.hits.sort(
            (a, b) => a._source.title.localeCompare(b._source.title)
        );

        setActiveResultsType('fetch');
        setRacesData(responses[0].hits);
        setDriversData(responses[1].hits);
        setTeamsData(responses[2].hits);
        setChampionshipsData(responses[3].hits);
        setCircuitsData(responses[4].hits);
        setActiveResultsType(false);
    };

    const handleLink = resultType => {
        const resultTypeData = {
            Races: { data: racesData, Component: RacesTable },
            Drivers: { data: driversData, Component: DriversTable },
            Teams: { data: teamsData, Component: TeamsTable },
            Championships: { data: championshipsData, Component: ChampionshipsTable },
            Circuits: { data: circuitsData, Component: CircuitsTable },
        };

        if (resultType === false) {
            setActiveResults(defaultResponse);
            setActiveResultsComponent(<></>);
        } else {
            const { data, Component } = resultTypeData[resultType];

            setActiveResults(data);
            setActiveResultsComponent(<Component results={data.hits} />);
        }

        setActiveResultsType(resultType);
    };

    const tableSummaryData = data => {
        return data.hits.slice(0, 4);
    };

    useEffect(() => {
        setSearchTerm(term);
        fetchResults();
    }, [results, term]);

    const summaryProps = { searchTerm, racesData, driversData, teamsData, championshipsData, circuitsData, handleLink };

    return (
        <div className="database-results">
            {activeResultsType && activeResults.hits.length > 0 ? (
                <ResultsTableWrapper
                    results={activeResults}
                    type={activeResultsType}
                    onClickHandler={handleLink}
                    searchList={true}>
                    {activeResultsComponent}
                </ResultsTableWrapper>
            ) : !activeResultsType && activeResultsType !== 'fetch' ? (
                <>
                    <DatabaseSummaryStats {...summaryProps} />
                    <ResultsTableWrapper results={racesData} type="Races" onClickHandler={handleLink}>
                        <RacesTable results={tableSummaryData(racesData)} />
                    </ResultsTableWrapper>
                    <ResultsTableWrapper results={driversData} type="Drivers" onClickHandler={handleLink}>
                        <DriversTable results={tableSummaryData(driversData)} />
                    </ResultsTableWrapper>
                    <ResultsTableWrapper results={teamsData} type="Teams" onClickHandler={handleLink}>
                        <TeamsTable results={tableSummaryData(teamsData)} />
                    </ResultsTableWrapper>
                    <ResultsTableWrapper results={championshipsData} type="Championships" onClickHandler={handleLink}>
                        <ChampionshipsTable results={tableSummaryData(championshipsData)} />
                    </ResultsTableWrapper>
                    <ResultsTableWrapper results={circuitsData} type="Circuits" onClickHandler={handleLink}>
                        <CircuitsTable results={tableSummaryData(circuitsData)} />
                    </ResultsTableWrapper>
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
};

export default DatabaseWrapper;
